import type { ReactNode } from "react";
import {
  BriefcaseBusiness,
  Calculator,
  ChartNoAxesCombined,
  ClipboardList,
  Home,
  LogOut,
  Menu,
  PackageCheck,
  Settings,
  Users,
} from "lucide-react";
import { Badge } from "./ui";
import type { TeamMember } from "../types";

export type AppView =
  | "dashboard"
  | "work"
  | "clients"
  | "budget"
  | "tools"
  | "plan"
  | "settings"
  | "more";

const desktopNavigation: Array<{
  id: AppView;
  label: string;
  icon: typeof Home;
}> = [
  { id: "dashboard", label: "Dashboard", icon: Home },
  { id: "work", label: "Daily work", icon: ClipboardList },
  { id: "clients", label: "Clients", icon: Users },
  { id: "budget", label: "Monthly budget", icon: Calculator },
  { id: "tools", label: "Tools & equipment", icon: PackageCheck },
  { id: "plan", label: "12-month plan", icon: ChartNoAxesCombined },
  { id: "settings", label: "Company & team", icon: Settings },
];

const mobileNavigation = [
  { id: "dashboard" as const, label: "Home", icon: Home },
  { id: "work" as const, label: "Work", icon: ClipboardList },
  { id: "clients" as const, label: "Clients", icon: Users },
  { id: "budget" as const, label: "Budget", icon: Calculator },
  { id: "more" as const, label: "More", icon: Menu },
];

export function AppShell({
  view,
  onView,
  profile,
  onSignOut,
  children,
}: {
  view: AppView;
  onView(view: AppView): void;
  profile: TeamMember;
  onSignOut(): void;
  children: ReactNode;
}) {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button className="brand-lockup" onClick={() => onView("dashboard")}>
          <img src="/brand/house-care-logo.jpeg" alt="House Care Management logo" />
          <span>
            <strong>House Care</strong>
            <small>Refilwe Garden Services</small>
          </span>
        </button>

        <nav className="sidebar-nav" aria-label="Main navigation">
          {desktopNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={view === item.id ? "active" : ""}
                onClick={() => onView(item.id)}
              >
                <Icon size={19} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-account">
          <div className="avatar" aria-hidden="true">
            {profile.display_name.slice(0, 1).toUpperCase()}
          </div>
          <div>
            <strong>{profile.display_name}</strong>
            <Badge tone={profile.role === "owner" ? "brand" : "neutral"}>
              {profile.role === "owner" ? "Owner" : "Team member"}
            </Badge>
          </div>
          <button className="icon-button" onClick={onSignOut} aria-label="Sign out">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <header className="mobile-header">
        <button className="mobile-brand" onClick={() => onView("dashboard")}>
          <BriefcaseBusiness size={20} />
          <span>House Care</span>
        </button>
        <button className="avatar avatar-button" onClick={() => onView("settings")}>
          {profile.display_name.slice(0, 1).toUpperCase()}
        </button>
      </header>

      <main className="app-main">{children}</main>

      <nav className="mobile-nav" aria-label="Mobile navigation">
        {mobileNavigation.map((item) => {
          const Icon = item.icon;
          const selected =
            view === item.id ||
            (item.id === "more" && ["tools", "plan", "settings"].includes(view));
          return (
            <button
              key={item.id}
              className={selected ? "active" : ""}
              onClick={() => onView(item.id)}
            >
              <Icon size={20} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
